import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Smartphone,
  Headphones,
  DownloadCloud,
  BarChart3,
  Palette,
  RefreshCw,
} from 'lucide-react';
import AppStoreBadges from '../components/AppStoreBadges';
import FeatureCard from '../components/FeatureCard';
import './Home.css';

function Home() {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  const iconColor = '#346751';
  const iconSize = 32;

  const features = [
    {
      icon: <Smartphone color={iconColor} size={iconSize} />,
      title: 'Truly Native Apps',
      description: 'Built with SwiftUI for iOS and Jetpack Compose for Android. No web wrappers—just smooth, responsive native performance.'
    },
    {
      icon: <Headphones color={iconColor} size={iconSize} />,
      title: 'Audiobooks & Ebooks',
      description: 'Listen to audiobooks with variable speed, sleep timers, and chapter navigation. Read EPUB and PDF files with highlights and bookmarks.'
    },
    {
      icon: <DownloadCloud color={iconColor} size={iconSize} />,
      title: 'Smart Downloads',
      description: 'Automatic downloads for in-progress items. WiFi-only option, storage quotas, and auto-cleanup keep your library organized.'
    },
    {
      icon: <BarChart3 color={iconColor} size={iconSize} />,
      title: 'Personal Stats',
      description: 'Track your listening time, sessions, and reading progress. See your patterns and celebrate your reading milestones.'
    },
    {
      icon: <Palette color={iconColor} size={iconSize} />,
      title: 'Deep Customization',
      description: 'Dark mode, custom themes, playback speed presets, and personalized home screen. Make the app truly yours.'
    },
    {
      icon: <RefreshCw color={iconColor} size={iconSize} />,
      title: 'Seamless Sync',
      description: 'Progress syncs instantly with your Audiobookshelf server. Pick up right where you left off on any device.'
    }
  ];

  return (
    <div className="home">
      <Helmet>
        <title>Audra Books - Read. Listen. Anywhere.</title>
        <meta name="description" content="Connect to your Audiobookshelf server and start enjoying your library. Listen to audiobooks, read ebooks, download for offline, and sync your progress across all devices." />
        <link rel="canonical" href="https://audrabooks.app/" />
        <meta property="og:title" content="Audra Books - Read. Listen. Anywhere." />
        <meta property="og:description" content="Connect to your Audiobookshelf server and start enjoying your library. Listen to audiobooks, read ebooks, and sync across all devices." />
        <meta property="og:url" content="https://audrabooks.app/" />
      </Helmet>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content animate-fade-in-up">
            <h1 className="hero-title">
              Read. Listen.
              <br />
              <span className="text-gradient">Anywhere.</span>
            </h1>
            <p className="hero-subtitle">
              Connect to your Audiobookshelf server and start enjoying your library.
              Listen to audiobooks, read ebooks, and sync your progress
              across all your devices.
            </p>
            <div className="hero-badges">
              <AppStoreBadges />
            </div>
          </div>
          <div className="hero-mockup animate-fade-in delay-300">
            <div className="mockup-container">
              {/* Left Phone */}
              <div className="hero-phone hero-phone-left">
                <img src="/images/screenshots/hero/Screenshot_20251230_004006.webp" alt="Audra Books app home screen" />
              </div>

              {/* Center Phone */}
              <div className="hero-phone hero-phone-center">
                <img src="/images/screenshots/hero/Screenshot_20251230_004048.webp" alt="Audra Books app book details" />
              </div>

              {/* Right Phone */}
              <div className="hero-phone hero-phone-right">
                <img src="/images/screenshots/hero/Screenshot_20251230_004145.webp" alt="Audra Books app statistics screen" />
              </div>
            </div>
          </div>

          {/* COMMENTED OUT: Original CSS mockups for potential future use
          <div className="hero-mockup animate-fade-in delay-300">
            <div className="mockup-container">
              <div className="phone-mockup phone-left">
                <div className="phone-screen">
                  <div className="mockup-content home-view">
                    <div className="home-header">
                      <div className="home-header-left">
                        <span className="home-greeting">Good evening</span>
                        <span className="home-username">Alex</span>
                      </div>
                      <div className="home-header-right">
                        <span className="home-stat-number">3</span>
                        <span className="home-stat-label">in progress</span>
                      </div>
                    </div>
                    <div className="home-section">
                      <div className="home-section-header">
                        <span className="section-icon">▶</span>
                        <span className="section-title">Continue Listening</span>
                      </div>
                      <div className="home-carousel">
                        <div className="home-book-card">
                          <div className="home-cover">
                            <img src="/images/covers/project-hail-mary.jpg" alt="Project Hail Mary" />
                            <span className="cover-badge progress">65%</span>
                          </div>
                          <div className="home-book-title">Project Hail Mary</div>
                          <div className="home-book-author">Andy Weir</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          END COMMENTED OUT */}
        </div>
        <div className="hero-scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Features Overview */}
      <section className="features-section section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Everything You Need</h2>
            <p>A complete audiobook and ebook experience, designed for your self-hosted library.</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="animate-on-scroll" style={{ transitionDelay: `${index * 100}ms` }}>
                <FeatureCard {...feature} />
              </div>
            ))}
          </div>
          <div className="features-cta-link animate-on-scroll">
            <Link to="/features" className="explore-features-btn">
              Explore All Features
            </Link>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="platform-section section">
        <div className="container">
          <div className="platform-content">
            <div className="platform-text animate-on-scroll">
              <h2>Native on iOS & Android</h2>
              <p>
                Same features, native feel. Audra Books delivers the same great
                experience on both platforms—built to feel right at home on your
                iPhone or Android. No shortcuts, no compromises.
              </p>
              <p>
                Connect to your Audiobookshelf server with your credentials or
                SSO (OIDC), and enjoy a unified library that syncs seamlessly
                across all your devices.
              </p>
            </div>
            <div className="platform-visual animate-on-scroll">
              <div className="dual-phone-mockup">
                <div className="platform-phone ios">
                  <img
                    src="/images/screenshots/iphonesimeshot_17.webp"
                    alt="Audra Books iOS app screenshot showing audiobook library"
                    className="platform-screenshot"
                  />
                  <div className="platform-label">iOS</div>
                </div>
                <div className="platform-phone android">
                  <img
                    src="/images/screenshots/new_Android_shot.webp"
                    alt="Audra Books Android app screenshot showing audiobook library"
                    className="platform-screenshot"
                  />
                  <div className="platform-label">Android</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="pricing-section section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <h2>Choose Your Experience</h2>
            <p>Free to get started, or unlock everything with Pro.</p>
          </div>
          <div className="pricing-grid">
            {/* Basic Tier */}
            <div className="pricing-card basic animate-on-scroll">
              <div className="pricing-header">
                <h3>Audra Basic</h3>
                <div className="pricing-price">Free</div>
                <p className="pricing-subtitle">Everything you need to start listening</p>
              </div>
              <ul className="pricing-features">
                <li><span className="check">✓</span> Stream your entire library</li>
                <li><span className="check">✓</span> Browse by authors, series & collections</li>
                <li><span className="check">✓</span> Full playback controls & sleep timer</li>
                <li><span className="check">✓</span> Chapter navigation</li>
                <li><span className="check">✓</span> 2 beautiful app themes</li>
                <li><span className="check">✓</span> Basic home screen</li>
              </ul>
            </div>

            {/* Pro Tier */}
            <div className="pricing-card pro animate-on-scroll" style={{ transitionDelay: '150ms' }}>
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-header">
                <h3>Audra Pro</h3>
                <div className="pricing-price">Pro</div>
                <p className="pricing-subtitle">The complete audiobook experience</p>
              </div>
              <ul className="pricing-features">
                <li className="category-label">Everything in Basic, plus:</li>

                <li className="category-header">Downloads & Offline</li>
                <li><span className="check pro">✓</span> Manual downloads</li>
                <li><span className="check pro">✓</span> Smart auto-downloads</li>
                <li><span className="check pro">✓</span> Offline playback</li>

                <li className="category-header">Personal Statistics</li>
                <li><span className="check pro">✓</span> Total listening time & active days</li>
                <li><span className="check pro">✓</span> Current & longest streaks</li>
                <li><span className="check pro">✓</span> Listening patterns & breakdown</li>
                <li><span className="check pro">✓</span> Books completed tracking</li>

                <li className="category-header">Ebooks & Reading</li>
                <li><span className="check pro">✓</span> EPUB & PDF support</li>
                <li><span className="check pro">✓</span> Highlights & annotations</li>
                <li><span className="check pro">✓</span> Full in-book search</li>
                <li><span className="check pro">✓</span> Cross-device bookmark sync</li>
                <li><span className="check pro">✓</span> Cross-device progress sync</li>

                <li className="category-header">AI & Insights</li>
                <li><span className="check pro">✓</span> AI-powered author bios</li>
                <li><span className="check pro">✓</span> Writing style insights</li>

                <li className="category-header">Customization</li>
                <li><span className="check pro">✓</span> 11 crafted app themes</li>
                <li><span className="check pro">✓</span> Custom playlists</li>
                <li><span className="check pro">✓</span> Home screen customization</li>
                <li><span className="check pro">✓</span> Technical audio details (codec, bitrate, size)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="download-section section" id="download">
        <div className="container">
          <div className="download-content animate-on-scroll">
            <h2>Start Listening Today</h2>
            <p>
              Download Audra Books and connect to your Audiobookshelf server.
              Your entire library, right in your pocket.
            </p>
            <AppStoreBadges size="large" />
            <div className="server-note">
              <span>Requires an </span>
              <a href="https://www.audiobookshelf.org" target="_blank" rel="noopener noreferrer">
                Audiobookshelf
              </a>
              <span> server</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
